import { useState } from 'react';
import { 
    Plus, 
    X,
    Calendar,
    Clock,
    Type
} from 'lucide-react';
import { Button } from '@mui/material';

type AddConditioningSessionProps = {
    sessionHolder: any;
    handleSubmit: (session: any) => void;
    handleCancel: () => void;
};

const AddConditioningSession: React.FC<AddConditioningSessionProps> = ({ 
    sessionHolder, 
    handleSubmit, 
    handleCancel 
}) => {
    const [session, setSession] = useState(sessionHolder);
    
    // Round down to nearest 5 minutes for start time
    const roundDownToFiveMinutes = (date: Date) => {
        const minutes = date.getMinutes();
        const roundedMinutes = Math.floor(minutes / 5) * 5;
        const newDate = new Date(date);
        newDate.setMinutes(roundedMinutes, 0, 0);
        return newDate;
    };

    const [startTime, setStartTime] = useState(() => {
        const now = roundDownToFiveMinutes(new Date());
        return now.toTimeString().slice(0, 5); // HH:MM format
    });

    const handleFieldChange = (fieldName: string, value: string) => {
        setSession({ ...session, [fieldName]: value });
    };

    const handleDateChange = (field: string, value: string) => {
        setSession({
            ...session,
            [field]: new Date(value).toISOString()
        });
    };

    const preSubmit = () => {
        const [hours, minutes] = startTime.split(':').map(Number);
        const sessionDate = new Date(session.start_time);
        sessionDate.setHours(hours, minutes, 0, 0);
        
        const newSession = {
            ...session,
            start_time: sessionDate.toISOString()
        };

        handleSubmit(newSession);
    };

    return (
        <div className="fixed inset-0 bg-black-500 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="bg-gradient-to-r from-cyan-500 to-cyan-600 p-6 text-black relative">
                    <div className="absolute inset-0 bg-white opacity-10"></div>
                    <div className="relative flex items-center justify-between">
                        <div className="flex items-center space-x-3 text-white">
                            <Plus className="w-6 h-6" />
                            <h2 className="text-xl font-bold">New Conditioning Session</h2>
                        </div>
                        <button
                            onClick={handleCancel}
                            className="w-8 h-8 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center transition-all duration-200"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Title Input */}
                    <div className="space-y-2">
                        <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                            <Type className="w-4 h-4" />
                            <span>Session Title</span>
                        </label>
                        <input
                            type="text"
                            value={session.title || ''}
                            onChange={(e) => handleFieldChange('title', e.target.value)}
                            placeholder="Enter session title"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none"
                            style={{ fontSize: '1rem' }}
                        />
                    </div>

                    {/* Start Date Input */}
                    <div className="space-y-2">
                        <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                            <Calendar className="w-4 h-4" />
                            <span>Start Date</span>
                        </label>
                        <input
                            type="date"
                            value={session.start_time ? session.start_time.split('T')[0] : ''}
                            onChange={(e) => handleDateChange('start_time', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none"
                            style={{ fontSize: '1rem' }}
                        />
                    </div>

                    {/* Start Time Input */}
                    <div className="space-y-2">
                        <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                            <Clock className="w-4 h-4" />
                            <span>Start Time</span>
                        </label>
                        <input
                            type="time"
                            value={startTime}
                            onChange={(e) => setStartTime(e.target.value)}
                            step="300" // 5 minute intervals
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none"
                            style={{ fontSize: '1rem' }}
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className='bottom-buttons'>
                    <Button
                        style={{margin: "10px 5px 5px", width: '100px'}}
                        disabled={session.title === null || session.title.length === 0}
                        onClick={preSubmit}
                        variant="contained"
                    >
                        Save
                    </Button>

                    <Button
                        style={{margin: "10px 5px 5px", width: '100px'}}
                        onClick={handleCancel}
                        variant="contained"
                    >
                        Cancel
                    </Button>
                </div> 
            </div>
        </div>
    );
};

export default AddConditioningSession;